// src/components/SyncStatusNotification.jsx
import { useState, useEffect } from 'react'
import { X, ArrowsClockwise, Warning, CheckCircle } from '@phosphor-icons/react'
import { getQueueSize, clearSyncQueue, forceSyncNow, onServiceWorkerMessage } from '../utils/serviceWorkerUtils'

export default function SyncStatusNotification() {
  const [queueSize, setQueueSize] = useState(0)
  const [syncing, setSyncing] = useState(false)
  const [lastSyncResult, setLastSyncResult] = useState(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Initial queue size check
    checkQueueSize()

    // Listen to service worker messages
    const cleanup = onServiceWorkerMessage((message) => {
      console.log('Service worker message:', message)
      
      switch (message.type) {
        case 'QUEUE_UPDATED':
          setQueueSize(message.queueSize)
          setVisible(message.queueSize > 0)
          break
          
        case 'SYNC_STARTED':
          setSyncing(true)
          setQueueSize(message.queueSize)
          setVisible(true)
          break
          
        case 'SYNC_COMPLETE':
          setSyncing(false)
          setQueueSize(message.queueSize)
          setLastSyncResult({
            success: message.successCount || 0,
            failed: message.failCount || 0
          })
          
          // Auto-hide after 3 seconds if queue is empty
          if (message.queueSize === 0) {
            setTimeout(() => setVisible(false), 3000)
          }
          break
          
        case 'QUEUE_CLEARED':
          setQueueSize(0)
          setSyncing(false)
          setVisible(false)
          break
      }
    })

    // Periodic queue check (every 30 seconds)
    const interval = setInterval(checkQueueSize, 30000)

    return () => {
      cleanup()
      clearInterval(interval)
    }
  }, [])

  async function checkQueueSize() {
    const size = await getQueueSize()
    setQueueSize(size)
    setVisible(size > 0)
  }

  async function handleClearQueue() {
    if (confirm('Clear all queued sync requests? This cannot be undone.')) {
      await clearSyncQueue()
      setQueueSize(0)
      setVisible(false)
    }
  }

  async function handleForceSync() {
    setSyncing(true)
    await forceSyncNow()
  }

  if (!visible) return null

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
      <div className={`rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 min-w-[300px] ${
        syncing 
          ? 'bg-blue-600 text-white' 
          : queueSize > 0 
          ? 'bg-yellow-500 text-white'
          : 'bg-green-600 text-white'
      }`}>
        {syncing ? (
          <>
            <ArrowsClockwise size={20} weight="bold" className="animate-spin" />
            <span className="font-medium flex-1">
              Syncing {queueSize} attendance...
            </span>
          </>
        ) : queueSize > 0 ? (
          <>
            <Warning size={20} weight="fill" />
            <span className="font-medium flex-1">
              {queueSize} pending sync{queueSize > 1 ? 's' : ''}
            </span>
            <button
              onClick={handleForceSync}
              className="text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-1 rounded transition-colors"
            >
              Sync Now
            </button>
          </>
        ) : (
          <>
            <CheckCircle size={20} weight="fill" />
            <span className="font-medium flex-1">
              All synced!
            </span>
          </>
        )}
        
        <button
          onClick={queueSize > 0 ? handleClearQueue : () => setVisible(false)}
          className="hover:bg-white hover:bg-opacity-20 p-1 rounded transition-colors"
          title={queueSize > 0 ? "Clear queue" : "Close"}
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {lastSyncResult && queueSize === 0 && (
        <div className="text-xs text-gray-600 text-center mt-1">
          ✓ {lastSyncResult.success} synced
          {lastSyncResult.failed > 0 && `, ${lastSyncResult.failed} failed`}
        </div>
      )}
    </div>
  )
}